import { SaveToLibrary } from "./SaveToLibrary";
import { workspaceBottomInset } from "./workspace-layout";
import { useEffect, useState } from "react";
import { useValue } from "@tldraw/state-react";
import type { WorkspaceModel } from "./useWorkspace";
import { PdfControls } from "./PdfControls";

/** Page controls belong to the selected PDF, independently of the inspector. */
export function PdfToolbar({
  model,
  onProperties,
}: {
  model: WorkspaceModel;
  onProperties: (id: string) => void;
}) {
  const { editor } = model;
  const [contextMenu, setContextMenu] = useState<{
    shapeId: string;
    x: number;
    y: number;
    nonce: number;
  } | null>(null);
  useEffect(() => {
    const container = editor.getContainer();
    const selectPdf = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('button,input,select,textarea,a,[role="dialog"]'))
        return;
      const shape = editor.getShapeAtPoint(
        editor.screenToPage({ x: event.clientX, y: event.clientY }),
        { hitInside: true },
      );
      const asset =
        shape?.type === "image" && shape.props.assetId
          ? editor.getAsset(shape.props.assetId)
          : undefined;
      if (!asset?.meta.pdfPages || shape?.meta.traceSnapshot) return;
      event.preventDefault();
      event.stopPropagation();
      editor.select(shape!.id);
      setContextMenu({
        shapeId: shape!.id,
        x: event.clientX,
        y: event.clientY,
        nonce: Date.now(),
      });
    };
    container.addEventListener("contextmenu", selectPdf, true);
    return () => container.removeEventListener("contextmenu", selectPdf, true);
  }, [editor]);
  const pdf = useValue(
    "selected PDF toolbar",
    () => {
      if (!editor.isIn("select")) return null;
      const shape = editor.getOnlySelectedShape();
      if (shape?.type !== "image" || !shape.props.assetId) return null;
      const asset = editor.getAsset(shape.props.assetId);
      if (!asset?.meta.pdfPages || shape.meta.traceSnapshot) return null;
      const bounds = editor.getShapePageBounds(shape);
      if (!bounds || !bounds.collides(editor.getViewportPageBounds()))
        return null;
      const viewport = editor.getViewportScreenBounds();
      const top = editor.pageToScreen({ x: bounds.center.x, y: bounds.minY });
      const bottom = editor.pageToScreen({
        x: bounds.center.x,
        y: bounds.maxY,
      });
      const width = Math.min(370, viewport.w - 32);
      const above = top.y - viewport.y - 112;
      const below = bottom.y - viewport.y + 64;
      const y =
        above >= 136
          ? above
          : Math.min(viewport.h - workspaceBottomInset(viewport.w) - 70, below);
      return {
        shape,
        width,
        x: Math.max(
          16,
          Math.min(viewport.w - width - 16, top.x - viewport.x - width / 2),
        ),
        y: Math.max(82, y),
        optionsAbove: y > viewport.h / 2,
      };
    },
    [editor],
  );
  useEffect(() => {
    if (contextMenu && contextMenu.shapeId !== pdf?.shape.id)
      setContextMenu(null);
  }, [contextMenu, pdf?.shape.id]);
  if (
    !pdf ||
    !model.canEdit ||
    pdf.shape.isLocked ||
    model.modal ||
    model.pickingTrace
  )
    return null;
  return (
    <div
      className="pdf-object-toolbar floating-surface"
      data-options-above={pdf.optionsAbove}
      style={{ left: pdf.x, top: pdf.y, width: pdf.width }}
    >
      <SaveToLibrary model={model} />
      <PdfControls
        key={pdf.shape.id}
        model={model}
        shape={pdf.shape}
        onProperties={() => onProperties(pdf.shape.id)}
        contextMenu={contextMenu?.shapeId === pdf.shape.id ? contextMenu : null}
      />
    </div>
  );
}
